'use client';

import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Label Variants
 *
 * Defines visual styles for the Label component.
 */
const labelVariants = cva(
  'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
  {
    variants: {
      size: {
        sm: 'text-xs',
        md: 'text-sm',
        lg: 'text-base',
      },
      weight: {
        normal: 'font-normal',
        medium: 'font-medium',
        semibold: 'font-semibold',
      },
    },
    defaultVariants: {
      size: 'md',
      weight: 'medium',
    },
  }
);

export interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    VariantProps<typeof labelVariants> {
  /** If true, renders as a required field indicator */
  required?: boolean;
  /** Optional helper text to display below the label */
  helperText?: React.ReactNode;
  /** Optional error message styling */
  error?: boolean;
}

/**
 * Label Component
 *
 * An accessible form label component built on Radix UI's Label primitive.
 * Supports sizing, weight variants, required indicators, and helper text.
 *
 * @example
 * ```tsx
 * // Basic label
 * <Label htmlFor="email">Email</Label>
 *
 * // Required field
 * <Label htmlFor="username" required>
 *   Username
 * </Label>
 *
 * // With helper text
 * <Label htmlFor="password" helperText="Min 8 characters">
 *   Password
 * </Label>
 *
 * // Error state
 * <Label htmlFor="confirm" error>
 *   Confirm Password
 * </Label>
 *
 * // Different sizes
 * <Label htmlFor="field1" size="lg">Large Label</Label>
 * <Label htmlFor="field2" size="sm">Small Label</Label>
 * ```
 */
const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(
  (
    {
      className,
      size,
      weight,
      required = false,
      helperText,
      error = false,
      children,
      ...props
    },
    ref
  ) => (
    <div className="space-y-1">
      <LabelPrimitive.Root
        ref={ref}
        className={cn(
          labelVariants({ size, weight }),
          error && 'text-error-600 dark:text-error-400',
          className
        )}
        {...props}
      >
        <span className="flex items-center gap-1">
          {children}
          {required && (
            <span
              className="text-error-500"
              aria-label="required"
              title="This field is required"
            >
              *
            </span>
          )}
        </span>
      </LabelPrimitive.Root>
      {helperText && (
        <p
          className={cn(
            'text-xs',
            error
              ? 'text-error-500 dark:text-error-400'
              : 'text-text-tertiary dark:text-text-secondary'
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  )
);

Label.displayName = LabelPrimitive.Root.displayName;

export { Label, labelVariants };
